import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import { Autocomplete, Box, Button, DialogActions, DialogContent, Grid, TextField } from "@mui/material";
import GenericTextField from "../../components/GenericTextField";

const VerbasManualFormSchema = z
  .object({
    idEsocialVerbaManual: z.number().readonly(),
    idTabelaRubrica: z.string({
      required_error: "Id da tabela de rubricas é obrigatório!",
      invalid_type_error: "Id da tabela de rubricas é obrigatório!"
    }).min(1, {
      message: "Id da tabela de rubricas é obrigatório!"
    }),
    codRubrica: z.string({
      required_error: "Código da rubrica é obrigatório!",
      invalid_type_error: "Código da rubrica é obrigatório!"
    }).min(1, {
      message: "Código da rubrica é obrigatório!"
    }),
    tipoRubrica: z.string({
      required_error: "Tipo de rubrica é obrigatório!",
      invalid_type_error: "Tipo de rubrica é obrigatório!"
    }).min(1, {
      message: "Tipo de rubrica é obrigatório!"
    }),
    descricaoRubrica: z.string(),
    codigoIncidencia: z.string(),
  })
  .partial({
    idEsocialVerbaManual: true,
    codigoIncidencia: true,
  })
  .required({
    idTabelaRubrica: true,
    codRubrica: true,
    tipoRubrica: true,
    descricaoRubrica: true,
  });

type VerbasManualFormData = z.infer<typeof VerbasManualFormSchema>;

type VerbasManualFormProps = {
  verbaManualValues: VerbasManualFormData;
  onSubmit(data: VerbasManualFormData): void;
  onClose(event: any): void;
};

export default function VerbasManualForm({
  verbaManualValues,
  onSubmit,
  onClose,
}: VerbasManualFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<VerbasManualFormData>({
    mode: "onChange",
    resolver: zodResolver(VerbasManualFormSchema),
    defaultValues: {
      idEsocialVerbaManual:
        verbaManualValues.idEsocialVerbaManual || undefined,
      idTabelaRubrica: verbaManualValues.idTabelaRubrica,
      codRubrica: verbaManualValues.codRubrica,
      tipoRubrica: verbaManualValues.tipoRubrica,
      descricaoRubrica: verbaManualValues.descricaoRubrica,
      codigoIncidencia: verbaManualValues.codigoIncidencia
    },
  });

  const optionsRubrica = [
    { id: 'P', label: 'Provento' },
    { id: 'D', label: 'Desconto' },
  ]

  const optionsCodIndCP = [
    { id : '00', label: '00 - Código de incidência tributária da rubrica para a Previdência Social - Não é base de cálculo' },
    { id : '01', label: '01 - Código de incidência tributária da rubrica para a Previdência Social - Não é base de cálculo em função de acordos internacionais de previdência social' },
    { id : '11', label: '11 - Base de cálculo das contribuições sociais - Salário de contribuição - Mensal' },
    { id : '12', label: '12 - Base de cálculo das contribuições sociais - Salário de contribuição - 13º salário' },
    { id : '13', label: '13 - Base de cálculo das contribuições sociais - Salário de contribuição - Exclusiva do empregador - Mensal' },
    { id : '14', label: '14 - Base de cálculo das contribuições sociais - Salário de contribuição - Exclusiva do empregador - 13° salário' },
    { id : '15', label: '15 - Base de cálculo das contribuições sociais - Salário de contribuição - Exclusiva do segurado - Mensal' },
    { id : '16', label: '16 - Base de cálculo das contribuições sociais - Salário de contribuição - Exclusiva do segurado - 13° salário' },
    { id : '21', label: '21 - Base de cálculo das contribuições sociais - Salário de contribuição - Salário-maternidade mensal, pago pelo empregador' },
    { id : '22', label: '22 - Base de cálculo das contribuições sociais - Salário de contribuição - Salário-maternidade 13º salário, pago pelo empregador' },
    { id : '23', label: '23 - Base de cálculo das contribuições sociais - Salário de contribuição - Auxilio doença mensal - Regime Próprio de Previdência Social' },
    { id : '24', label: '24 - Base de cálculo das contribuições sociais - Salário de contribuição - Auxilio doença 13o salário doença - Regime próprio de previdência social' },
    { id : '25', label: '25 - Base de cálculo das contribuições sociais - Salário de contribuição - Salário-maternidade mensal, pago pelo INSS' },
    { id : '26', label: '26 - Base de cálculo das contribuições sociais - Salário de contribuição - Salário-maternidade 13° salário, pago pelo INSS' },
    { id : '31', label: '31 - Contribuição descontada do segurado sobre salário de contribuição - Mensal' },
    { id : '32', label: '32 - Contribuição descontada do segurado sobre salário de contribuição - 13º salário' },
    { id : '34', label: '34 - Contribuição descontada do segurado sobre salário de contribuição - SEST' },
    { id : '35', label: '35 - Contribuição descontada do segurado sobre salário de contribuição - SENAT' },
    { id : '51', label: '51 - Contribuição descontada do segurado sobre salário de contribuição - Salário-família' },
    { id : '61', label: '61 - Contribuição descontada do segurado sobre salário de contribuição - Complemento de salário-mínimo - Regime próprio de previdência social' },
    { id : '91', label: '91 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - Mensal' },
    { id : '92', label: '92 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - 13º salário' },
    { id : '93', label: '93 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - Salário-maternidade' },
    { id : '94', label: '94 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - Salário-maternidade 13º salário' },
    { id : '95', label: '95 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - Exclusiva do empregador - Mensal' },
    { id : '96', label: '96 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - Exclusiva do empregador - 13º salário' },
    { id : '97', label: '97 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - Exclusiva do empregador - Salário-maternidade' },
    { id : '98', label: '98 - Suspensão de incidência sobre salário de contribuição em decorrência de decisão judicial - Exclusiva do empregador - Salário-maternidade 13º salário' }
  ]

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const handleTipoRubricaChange = (event: any) => {
    setValue("tipoRubrica", event?.id);
  }

  const handleCodigoIncidenciaChange = (event: any) => {
    setValue("codigoIncidencia", event?.id);
  }

  const submit = (data: VerbasManualFormData) => {
    const body: any = {
      verbaManual: {
        id_esocial_verba_manual: data.idEsocialVerbaManual,
        idTabelaRubrica: data.idTabelaRubrica,
        codRubrica: data.codRubrica,
        tipoRubrica: data.tipoRubrica,
        descricaoRubrica: data.descricaoRubrica,
        codigoIncidencia: data.codigoIncidencia || ''
      },
    };

    onSubmit(body);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="idEsocialVerbaManual"
                control={control}
                defaultValue={verbaManualValues.idEsocialVerbaManual}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    disabled
                    label="ID"
                    error={Boolean(errors.idEsocialVerbaManual)}
                    helperText={errors.idEsocialVerbaManual?.message}
                    placeholder={"Nova Verba Manual"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="idTabelaRubrica"
                control={control}
                defaultValue={verbaManualValues.idTabelaRubrica}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    required
                    label="ID da Tabela de Rubricas"
                    error={Boolean(errors.idTabelaRubrica)}
                    helperText={errors.idTabelaRubrica?.message}
                    placeholder={"ID Tabela Rubrica"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="codRubrica"
                control={control}
                defaultValue={verbaManualValues.codRubrica}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Código da Rubrica"
                    required
                    error={Boolean(errors.codRubrica)}
                    helperText={errors.codRubrica?.message}
                    placeholder={"Código Rubrica"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={9}>
              <Controller
                name="descricaoRubrica"
                control={control}
                defaultValue={verbaManualValues.descricaoRubrica}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Descrição da Rubrica"
                    required
                    error={Boolean(errors.descricaoRubrica)}
                    helperText={errors.descricaoRubrica?.message}
                    placeholder={"Descrição Rubrica"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="tipoRubrica"
                control={control}
                defaultValue={verbaManualValues.tipoRubrica}
                render={({ field: { ref, onChange, ...field } }) => (
                  <Autocomplete
                    renderInput={(params) => <TextField {...params} required label="Tipo da Rubrica" />}
                    options={optionsRubrica}
                    size={"small"}
                    value={optionsRubrica.find(item => item.label === verbaManualValues.tipoRubrica)}
                    onChange={(_, value) => handleTipoRubricaChange(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="codigoIncidencia"
                control={control}
                defaultValue={verbaManualValues.codigoIncidencia}
                render={({ field: { ref, ...field } }) => (
                  <Autocomplete
                    renderInput={(params) => <TextField {...params} label="Código de Incidência" />}
                    options={optionsCodIndCP}
                    size={"small"}
                    value={optionsCodIndCP.find(item => item.id === verbaManualValues.codigoIncidencia)}
                    onChange={(_, value) => handleCodigoIncidenciaChange(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
              onClick={()=>{console.log(errors);
              }}
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
